import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PricingCard from "./PricingCard";
import { PRICING_PLANS, getPlanConfiguration, calculateDiscountPercentage } from "../../config/pricingPlans";
import { BillingCycle } from "../../types/pricing";
import { Button } from "primereact/button";

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePlanSelect = (planId: string) => {
    const plan = PRICING_PLANS.find((p) => p.planId === planId);
    if (!plan) return;

    const pricing = billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice;
    const planConfig = getPlanConfiguration(planId, billingCycle, pricing.couponCode);
    if (planConfig) {
      navigate(planConfig.signupUrl, { state: { plan: planConfig } });
    }
  };

  const handleTabChange = (e: { index: number }) => {
    setActiveIndex(e.index);
    setBillingCycle(e.index === 0 ? "monthly" : "annual");
  };

  const isAnnual = billingCycle === "annual";

  return (
    <div className="pricing-page min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12"></div>

        <div className="max-w-6xl mx-auto mb-12">
          <div className="flex justify-center mb-6">
            <div className="inline-flex border border-gray-300 bg-white p-1 shadow-sm rounded-full overflow-hidden">
              <Button
                id="pp-btn-monthly"
                data-testid="pp-btn-monthly"
                onClick={() => handleTabChange({ index: 0 })}
                className={`px-8 py-4 text-xl font-semibold transition-all border-0 !rounded-l-full !rounded-r-none ${
                  activeIndex === 0 ? "bg-lime-500 text-white " : "bg-transparent text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl">Billed Monthly</div>
                  <div className="text-lg">(No Lock in Contract)</div>
                </div>
              </Button>
              <Button
                id="pp-btn-annual"
                data-testid="pp-btn-annual"
                onClick={() => handleTabChange({ index: 1 })}
                className={`px-8 py-4 text-xl font-semibold transition-all border-0 !rounded-r-full !rounded-l-none ${
                  activeIndex === 1 ? "bg-lime-500 text-white " : "bg-transparent text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl">Billed Annually</div>
                  <div className="text-lg">(Save up to 25%)</div>
                </div>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {PRICING_PLANS.map((plan) => {
            const pricing = billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice;
            const planConfig = getPlanConfiguration(plan.planId, billingCycle, pricing.couponCode);
            if (!planConfig) return null;

            const discountPercent = calculateDiscountPercentage(planConfig.originalPrice, planConfig.displayPrice);

            return (
              <PricingCard
                key={`${plan.planId}-${billingCycle}`}
                plan={planConfig}
                isAnnual={isAnnual}
                discountPercent={discountPercent}
                onSelect={() => handlePlanSelect(plan.planId)}
                isPopular={plan.isPopular}
              />
            );
          })}
        </div>

        <div className="text-center mt-12 text-gray-600">
          <p className="mb-2">Not sure which membership is right for you?</p>
          <p>
            Email us at{" "}
            <a
              id="pp-lnk-email"
              data-testid="pp-lnk-email"
              href="mailto:info@realestateinvestar.com.au"
              className="text-blue-600 hover:underline"
            >
              info@realestateinvestar.com.au
            </a>{" "}
            - we would love to help!
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
